/**
 * UserAvatar Component
 * Professional user avatar with status indicators for collaboration
 * Supports various sizes, status indicators, role badges, and interactive features
 */

import React, { useState } from 'react';
import { 
  UserIcon, 
  CheckCircleIcon, 
  ClockIcon, 
  ExclamationTriangleIcon,
  CogIcon,
  EllipsisVerticalIcon
} from '@heroicons/react/24/outline';
import { CollaborationUser } from '../../edgedocs/types/Collaboration.types';

export interface UserAvatarProps {
  user: CollaborationUser;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  showStatus?: boolean;
  showRole?: boolean;
  showTooltip?: boolean;
  clickable?: boolean;
  selected?: boolean;
  className?: string;
  style?: React.CSSProperties;
  onClick?: (user: CollaborationUser) => void;
  onManagePermissions?: (user: CollaborationUser) => void;
}

export interface UserAvatarTooltipProps {
  user: CollaborationUser;
  isVisible: boolean;
  position?: { x: number; y: number };
}

// Size configurations
const SIZE_CONFIGS = {
  xs: {
    avatar: 'w-6 h-6',
    text: 'text-xs',
    status: 'w-2 h-2',
    statusOffset: '-top-0.5 -right-0.5',
    roleText: 'text-[10px]',
    rolePadding: 'px-1',
  },
  sm: {
    avatar: 'w-8 h-8',
    text: 'text-sm',
    status: 'w-2.5 h-2.5',
    statusOffset: '-top-0.5 -right-0.5',
    roleText: 'text-xs',
    rolePadding: 'px-1.5',
  },
  md: {
    avatar: 'w-10 h-10',
    text: 'text-base',
    status: 'w-3 h-3',
    statusOffset: '-top-1 -right-1',
    roleText: 'text-xs',
    rolePadding: 'px-2',
  },
  lg: {
    avatar: 'w-12 h-12',
    text: 'text-lg',
    status: 'w-3.5 h-3.5',
    statusOffset: '-top-1 -right-1',
    roleText: 'text-sm',
    rolePadding: 'px-2',
  },
  xl: {
    avatar: 'w-16 h-16',
    text: 'text-xl',
    status: 'w-4 h-4',
    statusOffset: '-top-1 -right-1',
    roleText: 'text-sm',
    rolePadding: 'px-3',
  },
};

// Role color configurations
const ROLE_COLORS = {
  owner: {
    bg: 'bg-purple-100 dark:bg-purple-900/30',
    text: 'text-purple-700 dark:text-purple-300',
    ring: 'ring-purple-200 dark:ring-purple-700',
    icon: '👑',
  },
  editor: {
    bg: 'bg-lex-accent-soft',
    text: 'text-lex-accent',
    ring: 'ring-lex-accent',
    icon: '✏️',
  },
  reviewer: {
    bg: 'bg-yellow-100 dark:bg-yellow-900/30',
    text: 'text-yellow-700 dark:text-yellow-300',
    ring: 'ring-yellow-200 dark:ring-yellow-700',
    icon: '👀',
  },
  viewer: {
    bg: 'bg-gray-100 dark:bg-gray-700',
    text: 'text-gray-700 dark:text-gray-300',
    ring: 'ring-gray-200 dark:ring-gray-600',
    icon: '📖',
  },
};

// Status configurations
const STATUS_CONFIGS = {
  online: {
    color: 'bg-green-500',
    tooltip: 'Online',
    icon: CheckCircleIcon,
  },
  away: {
    color: 'bg-yellow-500',
    tooltip: 'Away',
    icon: ClockIcon,
  },
  busy: {
    color: 'bg-red-500',
    tooltip: 'Busy',
    icon: ExclamationTriangleIcon,
  },
  offline: {
    color: 'bg-gray-400',
    tooltip: 'Offline',
    icon: UserIcon,
  },
};

// Utility functions
const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const getUserStatus = (user: CollaborationUser): keyof typeof STATUS_CONFIGS => {
  // Priority 1: If user is currently active (real-time presence), show as online
  if (user.isCurrentlyActive) return 'online';
  
  // Priority 2: Use lastActive timestamp for inactive users
  if (!user.lastActive) return 'offline';
  
  const now = new Date();
  const lastActive = new Date(user.lastActive);
  const diffMinutes = (now.getTime() - lastActive.getTime()) / (1000 * 60);
  
  if (diffMinutes < 5) return 'online';
  if (diffMinutes < 30) return 'away';
  return 'offline';
};

const getAvatarBackground = (name: string): string => {
  // Generate consistent color based on name
  const colors = [
    'bg-red-500', 'bg-lex-accent', 'bg-green-500', 'bg-yellow-500',
    'bg-purple-500', 'bg-pink-500', 'bg-lex-accent', 'bg-teal-500',
  ];
  
  const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

// Tooltip component
const UserAvatarTooltip: React.FC<UserAvatarTooltipProps> = ({ 
  user, 
  isVisible, 
  position 
}) => {
  if (!isVisible) return null;

  const status = getUserStatus(user);
  const roleConfig = ROLE_COLORS[user.role];

  return (
    <div 
      className="fixed z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 min-w-64 max-w-80"
      style={{ 
        left: position?.x || 0, 
        top: position?.y || 0,
        transform: 'translate(-50%, -100%)',
        marginTop: '-8px'
      }}
    >
      {/* Header */}
      <div className="flex items-center space-x-3 mb-2">
        <div className={`${SIZE_CONFIGS.md.avatar} rounded-full ${getAvatarBackground(user.name)} flex items-center justify-center text-white font-medium`}>
          {user.avatar ? (
            <img 
              src={user.avatar} 
              alt={user.name}
              className="w-full h-full rounded-full object-cover"
            />
          ) : (
            <span className={SIZE_CONFIGS.md.text}>
              {getInitials(user.name)}
            </span>
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2">
            <h4 className="font-medium text-gray-900 dark:text-white truncate">
              {user.name}
            </h4>
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${roleConfig.bg} ${roleConfig.text}`}>
              <span className="mr-1">{roleConfig.icon}</span>
              {user.role}
            </span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
            {user.email}
          </p>
        </div>
      </div>

      {/* Status */}
      <div className="flex items-center space-x-2 mb-2">
        <div className={`w-2 h-2 rounded-full ${STATUS_CONFIGS[status].color}`}></div>
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {STATUS_CONFIGS[status].tooltip}
          {user.lastActive && status !== 'offline' && (
            <span className="ml-1">
              • Last seen {new Date(user.lastActive).toLocaleTimeString()}
            </span>
          )}
        </span>
      </div>

      {/* Permissions */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-2">
        <h5 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Permissions</h5>
        <div className="grid grid-cols-2 gap-1 text-xs">
          <div className={`flex items-center space-x-1 ${user.permissions.canEdit ? 'text-green-600' : 'text-gray-400'}`}>
            <span>{user.permissions.canEdit ? '✓' : '✗'}</span>
            <span>Edit</span>
          </div>
          <div className={`flex items-center space-x-1 ${user.permissions.canComment ? 'text-green-600' : 'text-gray-400'}`}>
            <span>{user.permissions.canComment ? '✓' : '✗'}</span>
            <span>Comment</span>
          </div>
          <div className={`flex items-center space-x-1 ${user.permissions.canTrackChanges ? 'text-green-600' : 'text-gray-400'}`}>
            <span>{user.permissions.canTrackChanges ? '✓' : '✗'}</span>
            <span>Track Changes</span>
          </div>
          <div className={`flex items-center space-x-1 ${user.permissions.canShare ? 'text-green-600' : 'text-gray-400'}`}>
            <span>{user.permissions.canShare ? '✓' : '✗'}</span>
            <span>Share</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main UserAvatar component
export const UserAvatar: React.FC<UserAvatarProps> = ({
  user,
  size = 'md',
  showStatus = true,
  showRole = false,
  showTooltip = true,
  clickable = false,
  selected = false,
  className = '',
  style,
  onClick,
  onManagePermissions,
}) => {
  const [showTooltipState, setShowTooltipState] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [showManageMenu, setShowManageMenu] = useState(false);
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);

  const sizeConfig = SIZE_CONFIGS[size];
  const status = getUserStatus(user);
  const statusConfig = STATUS_CONFIGS[status];
  const roleConfig = ROLE_COLORS[user.role];

  const handleMouseEnter = (event: React.MouseEvent) => {
    if (showTooltip) {
      // Clear any existing timeout
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
      
      const rect = event.currentTarget.getBoundingClientRect();
      setTooltipPosition({
        x: rect.left + rect.width / 2,
        y: rect.top - 10, // Add spacing above avatar to prevent interference
      });
      
      // Add small delay to prevent flickering
      const timeout = setTimeout(() => {
        setShowTooltipState(true);
      }, 300);
      setHoverTimeout(timeout);
    }
  };

  const handleMouseLeave = () => {
    // Clear timeout if leaving before tooltip shows
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    setShowTooltipState(false);
  };

  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (clickable && onClick) {
      onClick(user);
    }
  };

  const handleManageClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setShowManageMenu(!showManageMenu);
  };

  const handleManagePermissions = () => {
    setShowManageMenu(false);
    if (onManagePermissions) {
      onManagePermissions(user);
    }
  };

  return (
    <>
      <div 
        className={`relative inline-block ${className}`}
        style={style}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Main Avatar */}
        <div
          className={`
            ${sizeConfig.avatar} 
            rounded-full 
            ${getAvatarBackground(user.name)} 
            flex items-center justify-center 
            text-white font-medium
            transition-all duration-200
            ${clickable ? 'cursor-pointer hover:ring-4 hover:ring-lex-accent/30' : ''}
            ${selected ? 'ring-4 ring-lex-accent' : ''}
            ${roleConfig.ring ? `ring-2 ${roleConfig.ring}` : ''}
          `}
          onClick={handleClick}
        >
          {user.avatar ? (
            <img 
              src={user.avatar} 
              alt={user.name}
              className="w-full h-full rounded-full object-cover"
            />
          ) : (
            <span className={sizeConfig.text}>
              {getInitials(user.name)}
            </span>
          )}
        </div>

        {/* Status Indicator */}
        {showStatus && (
          <div 
            className={`
              absolute ${sizeConfig.statusOffset} 
              ${sizeConfig.status} 
              ${statusConfig.color} 
              rounded-full 
              border-2 border-white dark:border-gray-800
              ${status === 'online' ? 'animate-pulse' : ''}
            `}
            title={statusConfig.tooltip}
          />
        )}

        {/* Role Badge */}
        {showRole && (
          <div 
            className={`
              absolute -bottom-1 left-1/2 transform -translate-x-1/2
              ${roleConfig.bg} ${roleConfig.text}
              ${sizeConfig.roleText} ${sizeConfig.rolePadding}
              rounded-full font-medium
              border border-white dark:border-gray-800
              whitespace-nowrap
            `}
          >
            <span className="mr-1">{roleConfig.icon}</span>
            {user.role}
          </div>
        )}

        {/* Management Menu Button */}
        {onManagePermissions && (
          <div className="absolute -top-1 -right-1">
            <button
              onClick={handleManageClick}
              className="w-5 h-5 bg-gray-600 text-white rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors"
              title="Manage permissions"
            >
              <EllipsisVerticalIcon className="w-3 h-3" />
            </button>

            {/* Management Dropdown */}
            {showManageMenu && (
              <div className="absolute right-0 top-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 w-48">
                <div className="py-1">
                  <button
                    onClick={handleManagePermissions}
                    className="flex items-center w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <CogIcon className="w-4 h-4 mr-2" />
                    Manage Permissions
                  </button>
                  <button
                    onClick={() => {
                      setShowManageMenu(false);
                      // TODO: Implement view activity
                    }}
                    className="flex items-center w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <ClockIcon className="w-4 h-4 mr-2" />
                    View Activity
                  </button>
                  <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                  <button
                    onClick={() => {
                      setShowManageMenu(false);
                      // TODO: Implement remove user
                    }}
                    className="flex items-center w-full px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <ExclamationTriangleIcon className="w-4 h-4 mr-2" />
                    Remove Access
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tooltip */}
      {showTooltip && (
        <UserAvatarTooltip
          user={user}
          isVisible={showTooltipState}
          position={tooltipPosition}
        />
      )}
    </>
  );
};

// Avatar group component for multiple users
export interface UserAvatarGroupProps {
  users: CollaborationUser[];
  maxVisible?: number;
  size?: UserAvatarProps['size'];
  showStatus?: boolean;
  clickable?: boolean;
  className?: string;
  onUserClick?: (user: CollaborationUser) => void;
  onShowAll?: (users: CollaborationUser[]) => void;
}

export const UserAvatarGroup: React.FC<UserAvatarGroupProps> = ({
  users,
  maxVisible = 4,
  size = 'sm',
  showStatus = true,
  clickable = false,
  className = '',
  onUserClick,
  onShowAll,
}) => {
  const visibleUsers = users.slice(0, maxVisible);
  const hiddenCount = users.length - maxVisible;
  const sizeConfig = SIZE_CONFIGS[size];

  return (
    <div className={`flex -space-x-2 ${className}`}>
      {visibleUsers.map((user, index) => (
        <UserAvatar
          key={user.id}
          user={user}
          size={size}
          showStatus={showStatus}
          showTooltip={true}
          clickable={clickable}
          onClick={onUserClick}
          className="border-2 border-white dark:border-gray-800"
          style={{ zIndex: visibleUsers.length - index }}
        />
      ))}
      
      {hiddenCount > 0 && (
        <div
          className={`
            ${sizeConfig.avatar}
            rounded-full
            bg-gray-200 dark:bg-gray-700
            border-2 border-white dark:border-gray-800
            flex items-center justify-center
            text-gray-600 dark:text-gray-300
            font-medium
            ${sizeConfig.text}
            ${onShowAll ? 'cursor-pointer hover:bg-gray-300 dark:hover:bg-gray-600' : ''}
          `}
          onClick={() => onShowAll?.(users)}
          title={`${hiddenCount} more collaborator${hiddenCount > 1 ? 's' : ''}`}
        >
          +{hiddenCount}
        </div>
      )}
    </div>
  );
};

export default UserAvatar; 